import { useEffect, useState } from 'react';
import { apiBaseUrl, fetchAppCheckToken } from '../firebase';
import { ensurePublishedBrandingAdmin } from './secureFunctions';

export type BrandingConfig = {
  brandName: string;
  tagline: string;
  loginWelcome: string;
  signupWelcome: string;
  logoUrl?: string;
  authImageUrl?: string;
  updatedAt?: any;
  updatedBy?: string | null;
};

export const DEFAULT_BRANDING: BrandingConfig = {
  brandName: 'Belivah',
  tagline: 'INSPIRE. ENTERTAIN. CONNECT.',
  loginWelcome: 'Welcome back! Sign in to reconnect with your community and continue inspiring others.',
  signupWelcome: 'Welcome! Join a global community of believers. Share your story, connect, and be inspired.',
  logoUrl: undefined,
  authImageUrl: undefined,
};

export function useBrandingConfig(kind: 'published' | 'draft' = 'published') {
  const [config, setConfig] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!apiBaseUrl) {
          setConfig(DEFAULT_BRANDING);
          return;
        }

        const appCheckToken = await fetchAppCheckToken().catch(() => null);
        const headers: Record<string, string> = {};
        if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;

        const res = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/site/branding?kind=${encodeURIComponent(kind)}`, {
          method: 'GET',
          headers,
        });

        const json = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(json?.error || json?.code || 'failed-to-load-branding');
        }

        const data = json?.config as any;
        if (!active) return;
        setConfig({ ...DEFAULT_BRANDING, ...(data || {}) });
      } catch (e: any) {
        if (!active) return;
        setError(e?.message || String(e));
        setConfig(DEFAULT_BRANDING);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [kind]);

  return { config, loading, error };
}

// Helper to initialize published with defaults if missing
export async function ensurePublishedBranding() {
  try {
    await ensurePublishedBrandingAdmin();
  } catch {
    // Not signed in or not admin; ignore.
  }
}
